import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { updateMembershipLevelRenewalSettings } from '@/pages/MembershipLevelsPage.api'
import type { MembershipLevelsResponse } from '@/pages/MembershipLevelsPage.types'
import type { LevelRenewalSettingsDraft } from '@/pages/renewal-settings-support'
import { getJSON } from '@/lib/api'

export function useLevelRenewalOverrides() {
  const queryClient = useQueryClient()
  const membershipLevelsQuery = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const updateLevels = useMutation({
    mutationFn: async (drafts: Record<number, LevelRenewalSettingsDraft>) => {
      const levels = membershipLevelsQuery.data?.membership_levels ?? []
      const responses = await Promise.all(
        Object.entries(drafts).map(([id, draft]) => {
          const level = levels.find((candidate) => candidate.id === Number(id))
          if (!level) throw new Error('Membership Level is no longer available')
          return updateMembershipLevelRenewalSettings(level, draft)
        }),
      )
      return responses.map((response) => response.membership_level)
    },
    onSuccess: (levels) => {
      queryClient.setQueryData<MembershipLevelsResponse>(['membership-levels'], (current) => ({
        membership_levels: (current?.membership_levels ?? []).map(
          (level) => levels.find((updated) => updated.id === level.id) ?? level,
        ),
      }))
    },
  })

  return { levels: membershipLevelsQuery.data?.membership_levels ?? [], updateLevels }
}
